import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { RegionService } from '@6grain/masofa-monolith-api-client/dictionaries/api/region.service';
import { Region } from '@6grain/masofa-monolith-api-client/dictionaries/model/region';
import { AnalyticsCatalogService } from '../analytics-catalog.service';
import {
  ReportTemplateItem,
  SubscriptionItem,
  SubscriptionRunItem
} from '../models/analytics-catalog.models';
import { OrchestratorChatService } from '../orchestrator-chat.service';
import { ChatDatasetArtifact } from '../models/orchestrator-chat.models';

interface RegionOption {
  id: string;
  name: string;
}

/**
 * Subscription tab: template recipes with a weekday/time modal. Legacy snapshot rows still list.
 */
@Component({
  selector: 'app-subscriptions-tab',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  styleUrls: ['./catalog-tab.shared.scss'],
  template: `
    <section class="catalog-panel" data-tour="subscriptions-panel">
      <div class="panel-head">
        <div>
          <h2>{{ 'reporting_analytics.subscriptions_title' | translate }}</h2>
          <span class="muted">{{ 'reporting_analytics.subscriptions_hint' | translate }}</span>
        </div>
        <div class="filters">
          <select [(ngModel)]="activeFilter" (ngModelChange)="load()">
            <option value="">{{ 'reporting_analytics.filter_all' | translate }}</option>
            <option value="true">{{ 'reporting_analytics.status_active' | translate }}</option>
            <option value="false">{{ 'reporting_analytics.status_paused' | translate }}</option>
          </select>
          <button type="button" class="btn" (click)="load()" [disabled]="loading">
            <i class="pi pi-refresh" [class.pi-spin]="loading"></i>
          </button>
          <button type="button" class="btn primary" (click)="openCreate()">
            <i class="pi pi-plus"></i> {{ 'reporting_analytics.new_subscription' | translate }}
          </button>
        </div>
      </div>
      <div class="alert" *ngIf="error">{{ error }}</div>
      <div class="catalog-table-wrap" *ngIf="items.length; else noSubscriptions">
        <table class="catalog-table">
          <thead>
            <tr>
              <th>{{ 'reporting_analytics.col_template' | translate }}</th>
              <th>{{ 'reporting_analytics.col_format' | translate }}</th>
              <th>{{ 'reporting_analytics.col_schedule' | translate }}</th>
              <th>{{ 'reporting_analytics.col_channel' | translate }}</th>
              <th>{{ 'reporting_analytics.col_status' | translate }}</th>
              <th>{{ 'reporting_analytics.col_last' | translate }}</th>
              <th>{{ 'reporting_analytics.col_next' | translate }}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <ng-container *ngFor="let sub of items">
              <tr>
                <td>{{ templateTitle(sub) }}</td>
                <td>{{ formatLabel(sub.format) }}</td>
                <td>{{ scheduleLabel(sub) }}</td>
                <td>{{ 'reporting_analytics.channel_file' | translate }}</td>
                <td>
                  <span class="status" [attr.data-status]="sub.active ? 'active' : 'paused'">
                    {{ (sub.active ? 'reporting_analytics.status_active' : 'reporting_analytics.status_paused') | translate }}
                  </span>
                </td>
                <td class="muted">{{ sub.last_run_at_utc ? (sub.last_run_at_utc | date: 'dd.MM.yyyy') : '—' }}</td>
                <td class="muted">{{ sub.active && sub.next_run_at_utc ? (sub.next_run_at_utc | date: 'dd.MM.yyyy') : '—/—' }}</td>
                <td class="actions">
                  <button type="button" class="btn icon" (click)="toggleRuns(sub)"><i class="pi pi-chevron-down"></i></button>
                  <button type="button" class="btn icon" (click)="toggleActive(sub)">
                    <i class="pi" [class.pi-pause]="sub.active" [class.pi-play]="!sub.active"></i>
                  </button>
                  <button type="button" class="btn icon" (click)="openEdit(sub)"><i class="pi pi-pencil"></i></button>
                  <button type="button" class="btn icon danger" (click)="cancel(sub)"><i class="pi pi-trash"></i></button>
                </td>
              </tr>
              <tr class="runs-row" *ngIf="expandedId === sub.subscription_id">
                <td colspan="8">
                  <table class="runs-table" *ngIf="runs[sub.subscription_id] as list">
                    <tbody>
                      <tr *ngFor="let run of list">
                        <td>{{ run.started_at_utc | date: 'dd.MM.yyyy HH:mm' }}</td>
                        <td>{{ run.trigger_reason || '—' }}</td>
                        <td>{{ run.status }}</td>
                        <td>
                          <button type="button" class="btn icon" *ngIf="run.report_id" (click)="downloadRun(run)">
                            <i class="pi pi-download"></i>
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <span class="muted" *ngIf="!runs[sub.subscription_id]?.length">{{ 'reporting_analytics.no_runs' | translate }}</span>
                </td>
              </tr>
            </ng-container>
          </tbody>
        </table>
      </div>
      <ng-template #noSubscriptions>
        <div class="empty" *ngIf="!loading">
          <p>{{ 'reporting_analytics.no_subscriptions' | translate }}</p>
        </div>
      </ng-template>
      <p class="preview">{{ 'reporting_analytics.subscription_note' | translate }}</p>
    </section>

    <div class="modal-backdrop" *ngIf="showForm" (click)="showForm = false">
      <form class="modal" (click)="$event.stopPropagation()" (ngSubmit)="save()">
        <h3>{{ (editingId ? 'reporting_analytics.edit_subscription' : 'reporting_analytics.new_subscription') | translate }}</h3>
        <label *ngIf="catalogTemplate(form.templateId); else ownTemplateLabel">
          {{ 'reporting_analytics.field_template' | translate }}
          <select name="template" [(ngModel)]="form.templateId">
            <option *ngFor="let card of templates" [value]="card.template_id">{{ titleOf(card) }}</option>
          </select>
        </label>
        <ng-template #ownTemplateLabel>
          <label>
            {{ 'reporting_analytics.field_template' | translate }}
            <input [value]="editingTitle" disabled />
          </label>
        </ng-template>
        <label>
          {{ 'reporting_analytics.field_region' | translate }} *
          <select name="region" [(ngModel)]="form.geoId" required>
            <option value="">—</option>
            <option *ngFor="let region of regions" [value]="region.id">{{ region.name }}</option>
          </select>
        </label>
        <span class="field-label">{{ 'reporting_analytics.field_schedule' | translate }} *</span>
        <div class="weekday-row">
          <label *ngFor="let day of weekdays">
            <input type="checkbox" [checked]="form.days.includes(day.id)" (change)="toggleDay(day.id)" /> {{ day.label }}
          </label>
        </div>
        <div class="time-row">
          <label>
            {{ 'reporting_analytics.field_time' | translate }}
            <input type="time" name="time" [(ngModel)]="form.time" />
          </label>
          <label>
            {{ 'reporting_analytics.cron_or' | translate }}
            <input type="text" name="cron" class="mono" [(ngModel)]="form.cron" placeholder="0 4 * * 1" />
          </label>
        </div>
        <span class="field-label">{{ 'reporting_analytics.field_format' | translate }}</span>
        <div class="format-row">
          <label><input type="radio" name="fmt" value="pdf" [(ngModel)]="form.format" /> PDF</label>
          <label><input type="radio" name="fmt" value="xlsx" [(ngModel)]="form.format" /> Excel</label>
          <label><input type="radio" name="fmt" value="html" [(ngModel)]="form.format" /> HTML</label>
          <label><input type="radio" name="fmt" value="pptx" [(ngModel)]="form.format" /> PPTX</label>
        </div>
        <span class="field-label">{{ 'reporting_analytics.field_channel' | translate }}</span>
        <div class="format-row">
          <label><input type="radio" checked disabled /> {{ 'reporting_analytics.channel_file' | translate }}</label>
          <label class="soon"><input type="radio" disabled /> Telegram <small>{{ 'reporting_analytics.soon' | translate }}</small></label>
          <label class="soon"><input type="radio" disabled /> Email <small>{{ 'reporting_analytics.soon' | translate }}</small></label>
        </div>
        <label class="check">
          <input type="checkbox" name="pass" [(ngModel)]="form.triggerOnNewPass" />
          {{ 'reporting_analytics.field_trigger_pass' | translate }}
        </label>
        <p class="note">{{ 'reporting_analytics.pass_warning' | translate }}</p>
        <div class="alert" *ngIf="formError">{{ formError }}</div>
        <div class="form-actions">
          <button type="button" class="btn" (click)="showForm = false">{{ 'reporting_analytics.cancel' | translate }}</button>
          <button type="submit" class="btn primary" [disabled]="saving">{{ 'reporting_analytics.create_subscription' | translate }}</button>
        </div>
      </form>
    </div>
  `
})
export class SubscriptionsTabComponent implements OnInit, OnDestroy {
  @Input() latestDataset: ChatDatasetArtifact | null = null;
  @Input() locale = 'ru';

  items: SubscriptionItem[] = [];
  templates: ReportTemplateItem[] = [];
  regions: RegionOption[] = [];
  runs: Record<string, SubscriptionRunItem[]> = {};
  expandedId: string | null = null;
  activeFilter = '';
  loading = false;
  saving = false;
  showForm = false;
  editingId: string | null = null;
  editingTitle = '';
  error: string | null = null;
  formError: string | null = null;
  form = this.emptyForm();
  readonly weekdays = [
    { id: 1, label: 'Пн' },
    { id: 2, label: 'Вт' },
    { id: 3, label: 'Ср' },
    { id: 4, label: 'Чт' },
    { id: 5, label: 'Пт' },
    { id: 6, label: 'Сб' },
    { id: 0, label: 'Вс' }
  ];

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly catalog: AnalyticsCatalogService,
    private readonly chatApi: OrchestratorChatService,
    private readonly regionsApi: RegionService,
    private readonly translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.catalog.listTemplates().pipe(takeUntil(this.destroy$)).subscribe({
      next: (items) => (this.templates = items)
    });
    this.regionsApi
      .dictionariesRegionGetByQueryPost({
        take: 1000,
        offset: 0,
        filters: [{ filterField: 'level', filterValue: 2, filterOperator: 0 }]
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (rows) => (this.regions = this.mapRegions(rows)),
        error: () => (this.regions = [])
      });
    this.load();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  load(): void {
    this.loading = true;
    const active = this.activeFilter === '' ? undefined : this.activeFilter === 'true';
    this.catalog.listSubscriptions(active).pipe(takeUntil(this.destroy$)).subscribe({
      next: (items) => {
        this.items = items;
        this.loading = false;
        this.error = null;
      },
      error: (err: HttpErrorResponse) => {
        this.loading = false;
        this.error = this.describe(err);
      }
    });
  }

  openCreate(): void {
    this.editingId = null;
    this.form = this.emptyForm();
    this.formError = null;
    this.showForm = true;
  }

  openEdit(sub: SubscriptionItem): void {
    this.editingId = sub.subscription_id;
    this.editingTitle = this.templateTitle(sub);
    const params = this.readParams(sub.params);
    this.form = {
      templateId: sub.template_id || this.templates[0]?.template_id || 'region_summary',
      geoId: sub.geo_id || params.geo_id || '',
      days: [1],
      time: '09:00',
      cron: sub.cron || '',
      format: sub.format || 'pdf',
      triggerOnNewPass: sub.trigger_on_new_pass,
      seasonCycle: params.season_cycle || '1'
    };
    this.formError = null;
    this.showForm = true;
  }

  toggleDay(day: number): void {
    this.form.days = this.form.days.includes(day)
      ? this.form.days.filter((item) => item !== day)
      : [...this.form.days, day];
  }

  save(): void {
    this.formError = null;
    const known = this.catalogTemplate(this.form.templateId);
    if (known && !this.form.geoId) {
      this.formError = this.translate.instant('reporting_analytics.error_report_fields');
      return;
    }
    const region = this.regions.find((item) => item.id === this.form.geoId);
    const body = {
      title: known ? this.templateTitle({ template_id: this.form.templateId, title: null } as SubscriptionItem) : undefined,
      template_id: known ? this.form.templateId : undefined,
      format: this.form.format,
      delivery_channel: 'file',
      geo_id: this.form.geoId,
      geo_name: region?.name,
      season_cycle: this.form.seasonCycle,
      days: this.form.cron.trim() ? undefined : this.form.days,
      time: this.form.cron.trim() ? undefined : this.form.time,
      cron: this.form.cron.trim() || undefined,
      trigger_on_new_pass: this.form.triggerOnNewPass,
      locale: 'uz'
    };
    this.saving = true;
    const request = this.editingId
      ? this.catalog.updateSubscription(this.editingId, body)
      : this.catalog.createSubscription(body);
    request.pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.saving = false;
        this.showForm = false;
        this.load();
      },
      error: (err: HttpErrorResponse) => {
        this.saving = false;
        this.formError = this.describe(err);
      }
    });
  }

  toggleActive(sub: SubscriptionItem): void {
    this.catalog.updateSubscription(sub.subscription_id, { active: !sub.active }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (updated) => this.replace(updated),
      error: (err: HttpErrorResponse) => (this.error = this.describe(err))
    });
  }

  cancel(sub: SubscriptionItem): void {
    if (!confirm(this.translate.instant('reporting_analytics.confirm_cancel_subscription'))) {
      return;
    }
    this.catalog.cancelSubscription(sub.subscription_id).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => this.load(),
      error: (err: HttpErrorResponse) => (this.error = this.describe(err))
    });
  }

  toggleRuns(sub: SubscriptionItem): void {
    if (this.expandedId === sub.subscription_id) {
      this.expandedId = null;
      return;
    }
    this.expandedId = sub.subscription_id;
    this.catalog.listRuns(sub.subscription_id).pipe(takeUntil(this.destroy$)).subscribe({
      next: (runs) => (this.runs[sub.subscription_id] = runs),
      error: () => (this.runs[sub.subscription_id] = [])
    });
  }

  downloadRun(run: SubscriptionRunItem): void {
    if (run.report_id) {
      this.chatApi.downloadReport(run.report_id).subscribe();
    }
  }

  catalogTemplate(templateId: string): boolean {
    return this.templates.some((item) => item.template_id === templateId);
  }

  templateTitle(sub: SubscriptionItem): string {
    const card = this.templates.find((item) => item.template_id === sub.template_id);
    if (card) {
      return this.titleOf(card);
    }
    return sub.title || this.translate.instant('reporting_analytics.untitled_subscription');
  }

  titleOf(card: ReportTemplateItem): string {
    return this.translate.currentLang?.startsWith('uz') ? card.title_uz : card.title_ru;
  }

  formatLabel(format: string): string {
    return format === 'xlsx' ? 'Excel' : (format || '').toUpperCase();
  }

  scheduleLabel(sub: SubscriptionItem): string {
    if (sub.trigger_on_new_pass && !sub.cron) {
      return this.translate.instant('reporting_analytics.on_new_pass');
    }
    return sub.cron || '—';
  }

  private replace(updated: SubscriptionItem): void {
    this.items = this.items.map((item) => (item.subscription_id === updated.subscription_id ? updated : item));
  }

  private readParams(raw?: string | null): { geo_id?: string; season_cycle?: string } {
    if (!raw) {
      return {};
    }
    try {
      return JSON.parse(raw);
    } catch {
      return {};
    }
  }

  private mapRegions(rows: Region[] | { items?: Region[] } | null | undefined): RegionOption[] {
    const list = Array.isArray(rows) ? rows : rows?.items ?? [];
    return list
      .map((row) => ({
        id: String(row?.id ?? ''),
        name: row?.names?.['ru-RU'] || row?.names?.['uz-Latn-UZ'] || row?.names?.['en-US'] || String(row?.id ?? '')
      }))
      .filter((row) => row.id)
      .sort((a, b) => a.name.localeCompare(b.name, 'ru'));
  }

  private describe(err: HttpErrorResponse): string {
    const body = err?.error;
    return (typeof body === 'object' && (body?.error || body?.message)) || err?.message || 'Request failed';
  }

  private emptyForm() {
    return {
      templateId: 'weekly_digest',
      geoId: '',
      days: [1] as number[],
      time: '09:00',
      cron: '',
      format: 'pdf',
      triggerOnNewPass: false,
      seasonCycle: '1'
    };
  }
}
